/* ═══════════════════════════════════════════════════════════════
   TaskFields — the Task Sheet's title / status / priority / due /
   projects / description / assignees block. Shared by create AND
   view/edit (ONE panel, Decision 4). Kit components only: SearchableSelect
   (single + multiple), ui/input, kit RichText — no legacy hand-rolled
   fields, no <select>.
   ═══════════════════════════════════════════════════════════════ */
import type { Project, TaskPriority, TaskStatus } from '../../../../shared/types';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { RichText } from '../../../components/ui/rich-text';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { sanitizeProjectName } from '../../projects/helpers';
import { isArchived } from '../../../lib/archive';
import { STATUS_COLS, PRIORITY_LABELS } from '../types';

const PRIORITY_OPTIONS = (Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((value) => ({
  value,
  label: PRIORITY_LABELS[value],
}));

export interface AssigneeOption {
  value: string;
  label: string;
}

export interface TaskFieldsProps {
  editTitle: string;
  onTitleChange: (v: string) => void;
  editStatus: TaskStatus;
  onStatusChange: (v: TaskStatus) => void;
  editPriority: TaskPriority;
  onPriorityChange: (v: TaskPriority) => void;
  editDueDate: string;
  onDueDateChange: (v: string) => void;
  editProjectIds: string[];
  onProjectIdsChange: (ids: string[]) => void;
  editDesc: string;
  onDescChange: (v: string) => void;
  taskAssigneeIds: string[];
  onAssigneeIdsChange: (ids: string[]) => void;
  /** Options the viewer may pick FROM (assignableMembers), plus any
   *  currently-assigned member even if deactivated (Decision 21) so their
   *  name still resolves. */
  assigneeOptions: AssigneeOption[];
  /** False → the picker shows who's assigned but can't be changed (no
   *  assign_team/assign_anyone — Decision 12/14). */
  canPickAssignees: boolean;
  projects: Project[];
  /** Overall edit gate (canEditTask false, or create mode always true). */
  readOnly: boolean;
}

export function TaskFields({
  editTitle,
  onTitleChange,
  editStatus,
  onStatusChange,
  editPriority,
  onPriorityChange,
  editDueDate,
  onDueDateChange,
  editProjectIds,
  onProjectIdsChange,
  editDesc,
  onDescChange,
  taskAssigneeIds,
  onAssigneeIdsChange,
  assigneeOptions,
  canPickAssignees,
  projects,
  readOnly,
}: TaskFieldsProps) {
  return (
    <>
      {/* Title */}
      <div>
        <Label htmlFor="task-title">Title</Label>
        <Input
          id="task-title"
          value={editTitle}
          onChange={(e) => onTitleChange(e.target.value)}
          disabled={readOnly}
          style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}
          autoFocus
        />
      </div>

      {/* Status + Priority + Due row */}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <Label>Status</Label>
          <SearchableSelect
            value={editStatus}
            options={STATUS_COLS.map((s) => ({ value: s.id, label: s.label }))}
            onChange={(v) => onStatusChange(v as TaskStatus)}
            clearable={false}
            disabled={readOnly}
            searchPlaceholder="Search statuses…"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label>Priority</Label>
          <SearchableSelect
            value={editPriority}
            options={PRIORITY_OPTIONS}
            onChange={(v) => onPriorityChange(v as TaskPriority)}
            clearable={false}
            disabled={readOnly}
            searchPlaceholder="Search…"
          />
        </div>
        <div style={{ flex: 1 }}>
          <Label htmlFor="task-due">Due date</Label>
          <Input
            id="task-due"
            type="date"
            value={editDueDate}
            onChange={(e) => onDueDateChange(e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* Projects (multi-select) */}
      <div>
        <Label>Projects</Label>
        <SearchableSelect
          multiple
          value=""
          onChange={() => {}}
          values={editProjectIds}
          onChangeMulti={onProjectIdsChange}
          options={projects
            .filter(
              (p) => p.status === 'active' && (!isArchived(p) || editProjectIds.includes(p.id))
            )
            .map((p) => ({
              value: p.id,
              label: sanitizeProjectName(p.name) || 'Untitled',
              hint: p.projectNumber,
            }))}
          placeholder="No projects"
          searchPlaceholder="Search projects…"
          disabled={readOnly}
          ariaLabel="Projects"
        />
      </div>

      {/* Description */}
      <div>
        <Label>Description</Label>
        <RichText
          value={editDesc}
          onChange={onDescChange}
          placeholder="Add a description…"
          readOnly={readOnly}
          ariaLabel="Task description"
        />
      </div>

      {/* Assignees — always visible to anyone who can see the task
          (Decision 12); editable only with an assign permission. */}
      <div>
        <Label>Assigned to</Label>
        <SearchableSelect
          multiple
          value=""
          onChange={() => {}}
          values={taskAssigneeIds}
          onChangeMulti={onAssigneeIdsChange}
          options={assigneeOptions}
          placeholder="Unassigned"
          searchPlaceholder="Search team members…"
          disabled={readOnly || !canPickAssignees}
          ariaLabel="Assigned to"
        />
      </div>
    </>
  );
}
